/* eslint-disable react-hooks/exhaustive-deps */
import styled from '@emotion/styled';
import * as React from 'react';
import { useState, useEffect, useContext } from 'react';
import { useRouter } from 'next/router';
import H5 from '@base/H5/H5';
import H6 from '@base/H6/H6';
import H7 from '@base/H7/H7';
import P2 from '@base/P2/P2';
import boxShadowGenerator from '@utils/boxShadowGenerator';
import { Spacings, Fonts, breakpoints } from '@styles/index';
import useGoalEventProps from '@hooks/useGoalEventProps';
import Divider from '@base/Divider/Divider';
import { segment } from '@actions/analytics';
import {
  GROUP_JOIN_INVITATION_VIEWED,
  GROUP_JOIN_INVITATION_CLICKED,
  GROUP_WEEKLY_SCOREBOARD_CLICKED,
  PLANNER_PAGE,
  GROUP_CHANGE_CONFIRMED
} from '@constants/segment';
import Dynamic from '@base/Dynamic/Dynamic';
import { GroupsContext } from '@context/GroupsContext';
import useFetch from '@hooks/useFetch';
import useGroupsInvitationStore from '@stores/groups/groupsInvitationStore';
import useGroupsChangeStore from '@stores/groups/groupsChangeStore';
import usePracticeStore from '@stores/practice';
import GroupProfileRow from './WidgetCards/GroupProfileRow';
import GroupsActivityDrawer from './GroupsActivityDrawer';
import GroupsScoreboardCard from './CommonCards/GroupsScoreboardCard';
import GroupsLeaderboardCard from './WidgetCards/GroupsLeaderboardCard';
import GroupsJoinDialog from './WidgetCards/GroupsJoinDialog';
import EmptyGroupCard from './WidgetCards/EmptyGroupCard';
import { groupsDrawerTypes } from './groupsConstants';
import { getCurrentWeekString, getRemainingTimeString } from './groupsUtils';
import useGroupJoinInvite from './groups-hooks/useGroupJoinInvite';

const PracticeSelectorDialog = Dynamic(
  () => import('@cont/Groups/PracticeSelectorDialog')
);

interface IGroupsWidgetProps {
  goalUID: string;
  groupUID: string;
}

export const Wrapper = styled.div`
  display: flex;
  flex-direction: column;
  background: var(--color-base-1);
  padding: ${Spacings.SPACING_6B};
  border-radius: ${Spacings.SPACING_2B};
  box-shadow: ${boxShadowGenerator()};
  pointer-events: auto;
  min-width: 250px;
  max-width: 320px;
  cursor: pointer;
  @media only screen and (max-width: ${breakpoints.tablet}) {
    padding: ${Spacings.SPACING_6B} ${Spacings.SPACING_3B};
  }
  &:hover {
    box-shadow: ${boxShadowGenerator({
      yOffset: Spacings.SPACING_1B,
      blur: Spacings.SPACING_2B
    })};
  }
`;

export const StyH5 = styled(H5)`
  padding-bottom: ${Spacings.SPACING_4B};
  color: var(--color-text-primary);
`;

const LeaderboardHeading = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: ${Spacings.SPACING_4B};
`;

const TimeInfo = styled.span`
  font-size: ${Fonts.ULTRA_SMALL};
  color: var(--color-text-secondary);
`;

const Tooltip = styled.div`
  padding: ${Spacings.SPACING_4B};
  background: var(--color-i-black);
  border-radius: ${Spacings.SPACING_2B};
  margin-bottom: ${Spacings.SPACING_6B};
  color: var(--color-i-white);
  position: relative;
  :after {
    content: '';
    position: absolute;
    top: 100%;
    left: calc(100% - 30px);
    right: 0;
    width: 0;
    height: 0;
    border-top: solid 7px var(--color-i-black);
    border-left: solid 8px transparent;
    border-right: solid 8px transparent;
  }
`;

const StyH6 = styled(H6)`
  color: var(--color-i-white);
`;

const StyP2 = styled(P2)`
  margin-top: ${Spacings.SPACING_4B};
`;

const StyDivider = styled(Divider)`
  margin-top: ${Spacings.SPACING_5B};
  margin-bottom: ${Spacings.SPACING_5B};
`;

const GroupsWidget: React.FC<IGroupsWidgetProps> = ({
  goalUID,
  groupUID: currentGroupUID
}) => {
  const groupsContext = useContext(GroupsContext);

  const {
    startDate,
    endDate,
    currentDate,
    leaderboardData,
    users = [],
    meUserRank
  } = groupsContext;

  const router = useRouter();
  const { query } = useRouter();
  const { inviteeUID, showgroups, goalSlug } = query;

  // @ts-ignore
  const { practiceSelectedTopic: selectedTopic, setPracticeSelectedTopic } =
    usePracticeStore();
  const { data: inviteeInfo, isLoading } = useFetch(
    inviteeUID ? `v1/group/invitee_info?invitee_uid=${inviteeUID}` : null,
    {},
    {}
  );

  const goalSegmentData = useGoalEventProps({ goalUID });
  const showGroupsResizeAwareness =
    showgroups && (showgroups === 'true' || showgroups === 'True');

  const [drawerType, setDrawerType] = useState(
    !currentGroupUID ? groupsDrawerTypes.JOIN_GROUP : groupsDrawerTypes.DEFAULT
  );
  const [isDrawerVisible, setIsDrawerVisible] = useState(
    showGroupsResizeAwareness
  );
  const [showInviteCard, setShowInviteCard] = useState(false);
  const [shouldJoinGroupViaInvite, setShouldJoinGroupViaInvite] =
    useState(false);

  const { isGroupNew, toggleIsGroupNew } = useGroupsChangeStore();
  const { groupJoinError } = useGroupJoinInvite(shouldJoinGroupViaInvite);
  const { isGroupInvitationReceived, toggleGroupInvitationReceived } =
    useGroupsInvitationStore();

  // Open Group Join/Change Dialog when invitation is received.
  useEffect(() => {
    if (!isLoading && !!inviteeInfo) {
      toggleGroupInvitationReceived(true);
    }
  }, [isLoading, inviteeInfo]);

  useEffect(() => {
    if (showGroupsResizeAwareness) {
      setIsDrawerVisible(showGroupsResizeAwareness);
    }
  }, [showGroupsResizeAwareness]);

  useEffect(() => {
    if (isGroupInvitationReceived && !groupJoinError && currentGroupUID) {
      segment.track(GROUP_JOIN_INVITATION_VIEWED, {
        ...goalSegmentData,
        last_primary_source: PLANNER_PAGE,
        last_primary_source_section: 'Planner',
        group_size: users.length
      });
    }
  }, [isGroupInvitationReceived, groupJoinError]);

  const addInviteCard = () => setShowInviteCard(true);
  const closeDrawer = () => {
    setIsDrawerVisible(false);
    setShowInviteCard(false);
    if (showGroupsResizeAwareness) {
      router.replace(
        '/goal/[goalSlug]/[goalUID]/planner',
        `/goal/${goalSlug}/${goalUID}/planner`,
        { shallow: true }
      );
    }
  };
  const openDrawer = () => setIsDrawerVisible(true);
  const closeOnbaordingMessage = () => {
    toggleIsGroupNew(false);
  };

  const onProfileClick = () => {
    setDrawerType(groupsDrawerTypes.DEFAULT);
    openDrawer();
    closeOnbaordingMessage();
    segment.track(GROUP_WEEKLY_SCOREBOARD_CLICKED, {
      ...goalSegmentData,
      last_primary_source: PLANNER_PAGE,
      last_primary_source_section: 'Planner',
      group_size: users.length
    });
  };

  const closeSelector = () => setPracticeSelectedTopic({});

  const onJoinGroupViaInvite = () => {
    if (currentGroupUID) {
      segment.track(GROUP_CHANGE_CONFIRMED, {
        ...goalSegmentData,
        last_primary_source: PLANNER_PAGE,
        last_primary_source_section: 'Planner',
        group_size: users.length,
        group_change_reason: 'group_invite'
      });
    }

    router.replace(
      '/goal/[goalSlug]/[goalUID]',
      `/goal/${goalSlug}/${goalUID}`,
      { shallow: true }
    );

    setShouldJoinGroupViaInvite(true);
    closeDrawer();
  };

  const onClickAccept = () => {
    segment.track(GROUP_JOIN_INVITATION_CLICKED, {
      ...goalSegmentData,
      last_primary_source: PLANNER_PAGE,
      last_primary_source_section: 'Planner',
      group_size: users.length
    });

    if (!currentGroupUID) {
      onJoinGroupViaInvite();
    } else {
      openDrawer();
      setDrawerType(groupsDrawerTypes.CHANGE_GROUP);
    }
  };

  const renderLeaderboardInfo = () => {
    if (leaderboardData) {
      return (
        <GroupsLeaderboardCard
          startDate={startDate}
          endDate={endDate}
          leaderboard={leaderboardData}
          goalUID={goalUID}
          meUserRank={meUserRank}
        />
      );
    }

    return (
      <>
        <StyDivider />
        <div>
          <LeaderboardHeading>
            <H7>Week of {getCurrentWeekString(startDate, endDate)}</H7>
            <TimeInfo>
              Ends in {getRemainingTimeString(currentDate, endDate)}
            </TimeInfo>
          </LeaderboardHeading>
          <GroupsScoreboardCard type="widget" />
        </div>
      </>
    );
  };

  const renderGroupsWidget = () => {
    if (!currentGroupUID)
      return <EmptyGroupCard onLearnWithGroup={openDrawer} />;

    return (
      <Wrapper onClick={onProfileClick}>
        <StyH5>Group</StyH5>
        {isGroupNew && (
          <Tooltip>
            <StyH6>
              Welcome to your new group! Click here to see what they are
              studying
            </StyH6>
          </Tooltip>
        )}
        <GroupProfileRow
          isDrawerVisible={isDrawerVisible}
          closeDrawer={closeDrawer}
          goalUID={goalUID}
          onProfileClick={onProfileClick}
          addInviteCard={addInviteCard}
        />
        {users.length <= 3 && (
          <StyP2 color="var(--color-text-secondary)">
            More members will be added soon
          </StyP2>
        )}
        {renderLeaderboardInfo()}
      </Wrapper>
    );
  };

  return (
    <>
      {renderGroupsWidget()}
      {!!selectedTopic?.uid && (
        <PracticeSelectorDialog
          // @ts-ignore
          drawerState={!!selectedTopic?.uid}
          onClose={closeSelector}
        />
      )}
      <GroupsActivityDrawer
        drawerState={isDrawerVisible}
        closeDrawerHandler={closeDrawer}
        goalUID={goalUID}
        type={drawerType}
        onChangeGroup={onJoinGroupViaInvite}
        showInviteCard={showInviteCard}
        setShowInviteCard={setShowInviteCard}
      />
      <GroupsJoinDialog
        open={isGroupInvitationReceived && !groupJoinError && !!inviteeInfo}
        title={`Invite from ${inviteeInfo?.firstName}`}
        description="Start learning together, compete with them in weekly group competitions"
        acceptOnClick={onClickAccept}
      />
      <GroupsJoinDialog
        open={isGroupInvitationReceived && !!groupJoinError}
        title={groupJoinError?.title}
        description={groupJoinError?.description}
        type="error"
      />
    </>
  );
};

export default GroupsWidget;
